import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { createFanTag, deleteFanTag, fetchAvailableFanTags, fetchFanTags } from '@/lib/api/audience';
import { EVERYONE_TAG } from '../constants';
import { useStanQuery } from '@/queries';
import { QUERY_KEYS, SENTRY_FEATURES } from '@/lib/constants';

export interface UseCustomerTagsResult {
    customerTags: string[];
    availableTags: string[];
    updateTagsMutation: UseMutationResult<string[], Error, string[]>;
}

export function useCustomerTags(userFanId: number, enabled: boolean): UseCustomerTagsResult {
    const queryClient = useQueryClient();

    // Use React Query for fetching available tags
    const availableTagsQuery = useStanQuery(
        {
            queryKey: [QUERY_KEYS.FAN_TAGS, 'available'],
            queryFn: fetchAvailableFanTags,
            enabled: enabled && !!userFanId,
            select: (data: string[]): string[] => data,
            staleTime: 5 * 60 * 1000, // 5 minutes
            retry: 2,
            meta: {
                error: {
                    sentryMessage: '[AUDIENCE] failed-to-fetch-available-tags',
                    feature: SENTRY_FEATURES.AUDIENCE,
                    title: 'Unable to load tags',
                    message: 'Unable to load available tags. Please try again.',
                },
            },
        },
        false
    );

    // Use React Query for fetching current customer tags
    const customerTagsQuery = useStanQuery(
        {
            queryKey: [QUERY_KEYS.FAN_TAGS, 'customer', userFanId],
            queryFn: () => fetchFanTags(userFanId),
            enabled: enabled && !!userFanId,
            select: (data: string[]): string[] => data,
            staleTime: 5 * 60 * 1000, // 5 minutes
            retry: 2,
            meta: {
                error: {
                    sentryMessage: '[AUDIENCE] failed-to-fetch-customer-tags',
                    feature: SENTRY_FEATURES.AUDIENCE,
                    title: 'Unable to load tags',
                    message: 'Unable to load customer tags. Please try again.',
                },
            },
        },
        false
    );

    // Update tags mutation
    const updateTagsMutation = useMutation({
        mutationFn: async (next: string[]) => {
            let normalizedNext = Array.from(new Set(next));
            // If "Everyone" tag is selected, only keep that tag
            if (normalizedNext.includes(EVERYONE_TAG)) {
                normalizedNext = [EVERYONE_TAG];
            }
            const currentTags = customerTagsQuery.data || [];
            const added = normalizedNext.filter(tag => !currentTags.includes(tag));
            const removed = currentTags.filter(tag => !normalizedNext.includes(tag));

            // Parallelize tag creation and deletion operations
            await Promise.all([
                ...added.map(tag => createFanTag(userFanId, tag)),
                ...removed.map(tag => deleteFanTag(userFanId, tag)),
            ]);

            return normalizedNext;
        },
        onSuccess: () => {
            // Invalidate and refetch customer tags
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FAN_TAGS, 'customer', userFanId] });
        },
    });

    return {
        customerTags: customerTagsQuery.data || [],
        availableTags: availableTagsQuery.data || [],
        updateTagsMutation,
    };
}
